import React, { useEffect, useState } from 'react';
import API_BASE from '../../utils/api.js';
import notify from '../../utils/notify';
import ProposalPdfDocument from '../Projects/Proposals/ProposalPdfDocument';
import RichTextEditor from '../shared/RichTextEditor';
import '../../component-styles/ProposalTemplateSettings.css';

const DEFAULT_ACCENT_COLOR = '#a5d27d';

const DEFAULTS = {
  termsText: '',
  showDimensions: true,
  showColorMaterial: true,
  showCustomSpecs: true,
  showProductLink: false,
  showMsrpSavings: false,
  showVendor: false,
  requireSpecConfirmation: false,
  defaultNotes: '',
  requirePaymentOnApproval: true,
  defaultEmailSubject: '',
  accentColor: DEFAULT_ACCENT_COLOR,
};

const SAMPLE_ITEMS = [
  {
    itemId: 'sample-1',
    name: 'Sofa — Custom Upholstery',
    description: 'Performance velvet, brass legs',
    room: 'Living Room',
    qty: 1,
    clientUnit: 2450,
    clientShipping: 150,
    lineTotal: 2600,
    msrp: 3200,
    color: 'Sage Green',
    materials: 'Performance Velvet',
    customSpecs: [
      { id: 'sample-spec-1', label: 'COM Yardage', value: '29.5 yards' },
      { id: 'sample-spec-2', label: 'Leg', value: 'Antique Brass' },
    ],
    dimensions: { width: 84, height: 34, depth: 38 },
    link: 'https://example.com/product/sofa',
  },
];

const SAMPLE_TOTALS = { subtotal: 2600, tax: 208, grandTotal: 2808 };
const SAMPLE_FORM = { client: { name: 'Jane Client' }, billingAddress: '123 Main St\nAnytown, ST 00000' };
const SAMPLE_COMPANY = { companyName: 'Your Studio Name' };

export default function ProposalTemplateSettings() {
  const userId = localStorage.getItem('userId');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULTS);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API_BASE}/api/settings/${userId}`);
        const data = await res.json();
        setForm({
          termsText: data.proposalTemplate?.termsText || DEFAULTS.termsText,
          showDimensions: data.proposalTemplate?.showDimensions !== false,
          showColorMaterial: data.proposalTemplate?.showColorMaterial !== false,
          showCustomSpecs: data.proposalTemplate?.showCustomSpecs !== false,
          showProductLink: data.proposalTemplate?.showProductLink === true,
          showMsrpSavings: data.proposalTemplate?.showMsrpSavings === true,
          showVendor: data.proposalTemplate?.showVendor === true,
          requireSpecConfirmation: data.proposalTemplate?.requireSpecConfirmation === true,
          defaultNotes: data.proposalTemplate?.defaultNotes || DEFAULTS.defaultNotes,
          requirePaymentOnApproval: data.proposalTemplate?.requirePaymentOnApproval !== false,
          defaultEmailSubject: data.proposalTemplate?.defaultEmailSubject || DEFAULTS.defaultEmailSubject,
          accentColor: data.proposalTemplate?.accentColor || DEFAULT_ACCENT_COLOR,
        });
      } catch (err) {
        console.error('Failed to load proposal template settings', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [userId]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('idToken');
      const res = await fetch(`${API_BASE}/api/settings/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ proposalTemplate: form }),
      });
      if (!res.ok) throw new Error('Save failed');
      notify('Proposal template saved!');
    } catch (err) {
      console.error('Save proposal template failed', err);
      notify('Failed to save proposal template.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="pt-settings">
      <p className="pt-desc">
        Customize what shows on your proposals — the PDF, the link you send clients, and the client portal all follow these settings.
      </p>

      <div className="pt-grid">
        {/* Left — Controls */}
        <div className="pt-controls">
          <div className="form-row">
            <label>Terms &amp; Conditions</label>
            <RichTextEditor
              rows={4}
              value={form.termsText}
              onChange={(html) => set('termsText', html)}
              placeholder="e.g. Prices valid for 30 days. All sales final on special-order items."
            />
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
              Shown at the bottom of the proposal — this is what the client is agreeing to when they approve.
            </p>
          </div>

          <div className="form-row">
            <label>Accent Color</label>
            <div className="pt-color-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="color"
                value={form.accentColor}
                onChange={(e) => set('accentColor', e.target.value)}
              />
              <input
                type="text"
                value={form.accentColor}
                onChange={(e) => set('accentColor', e.target.value)}
                maxLength={7}
                placeholder={DEFAULT_ACCENT_COLOR}
                style={{ width: 100 }}
              />
            </div>
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
              Used for totals, highlights, and buttons on the proposal PDF and client-facing page.
            </p>
          </div>

          <p className="pt-section-label">Line item details</p>

          <div className="form-row pt-checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={form.showDimensions}
                onChange={(e) => set('showDimensions', e.target.checked)}
              />
              Show dimensions
            </label>
          </div>

          <div className="form-row pt-checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={form.showColorMaterial}
                onChange={(e) => set('showColorMaterial', e.target.checked)}
              />
              Show color / material / finish
            </label>
          </div>

          <div className="form-row pt-checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={form.showCustomSpecs}
                onChange={(e) => set('showCustomSpecs', e.target.checked)}
              />
              Show custom specs
            </label>
          </div>

          <div className="form-row pt-checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={form.showProductLink}
                onChange={(e) => set('showProductLink', e.target.checked)}
              />
              Show a link to the product (for retail purchases)
            </label>
          </div>

          <div className="form-row pt-checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={form.showMsrpSavings}
                onChange={(e) => set('showMsrpSavings', e.target.checked)}
              />
              Show MSRP and client savings
            </label>
          </div>

          <div className="form-row pt-checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={form.showVendor}
                onChange={(e) => set('showVendor', e.target.checked)}
              />
              Show vendor name to client
            </label>
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
              Off by default — most studios don't want clients to see which vendor an item is sourced from.
            </p>
          </div>

          <div className="form-row pt-checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={form.requireSpecConfirmation}
                onChange={(e) => set('requireSpecConfirmation', e.target.checked)}
              />
              Require client to confirm item details are correct before approving
            </label>
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
              Adds a checkbox to the approval step asking the client to confirm the dimensions, color/material, and other details shown are correct.
            </p>
          </div>

          <p className="pt-section-label">Approval &amp; payment</p>

          <div className="form-row pt-checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={form.requirePaymentOnApproval}
                onChange={(e) => set('requirePaymentOnApproval', e.target.checked)}
              />
              Let clients pay a deposit when approving a proposal
            </label>
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
              Turn this off if your studio only asks clients to approve the proposal, then follows up with a separate invoice for payment.
            </p>
          </div>

          <p className="pt-section-label">Defaults</p>

          <div className="form-row">
            <label>Default Notes / Payment Terms</label>
            <RichTextEditor
              rows={3}
              value={form.defaultNotes}
              onChange={(html) => set('defaultNotes', html)}
              placeholder="Pre-fills the Notes / Payment Terms field on new proposals."
            />
          </div>

          <div className="form-row">
            <label>Default Email Subject</label>
            <input
              type="text"
              value={form.defaultEmailSubject}
              onChange={(e) => set('defaultEmailSubject', e.target.value)}
              placeholder='e.g. You&apos;ve received a Proposal from {companyName}'
            />
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
              Use <code>{'{companyName}'}</code> and <code>{'{proposalNumber}'}</code> as placeholders. Leave blank for the default subject.
            </p>
          </div>

          <button className="save-btn" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>

        {/* Right — Live Preview */}
        <div className="pt-preview">
          <p className="pt-preview-label">Preview</p>
          <div className="pt-preview-frame">
            <div className="pt-preview-scale">
              <ProposalPdfDocument
                proposalNumber="00001"
                computedItems={SAMPLE_ITEMS}
                totals={SAMPLE_TOTALS}
                form={SAMPLE_FORM}
                project={{}}
                company={SAMPLE_COMPANY}
                apiBase={API_BASE}
                template={form}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
